import { __assign, __awaiter, __generator } from "tslib";
import { useOfficeComponentContext } from '@office-start/component-context-service';
import { useMutation } from '@office-start/data-fetching';
import { getCurrentDate } from '@office-start/date-utility';
import { useEdgeworthConfig } from '@office-start/edgeworth-config-service';
import { useIdentity } from '@office-start/identity-service';
import { useCallback, useMemo } from 'react';
import { getBaseRequestData } from '../operations/getBaseRequestData';
import { hideItem, unhideItem } from '../operations/hideItem';
import { pinItem, unpinItem } from '../operations/pinItem';
import { useEdgeworthDataStore } from '../store/useEdgeworthDataStore';
import { useEdgeworthStoreDocumentUpdater } from '../store/useEdgeworthDocumentsStore';
import { getSpoId } from '../utilities/getSpoId';
import { handleEdgeworthRequestError } from '../utilities/handleEdgeworthRequestError';
export function useMutateEdgeworthItem() {
    var _this = this;
    var context = useOfficeComponentContext().data;
    var config = useEdgeworthConfig().data;
    var identity = useIdentity().data;
    /**
     * updateDocument and updateFile below are used to apply in-memory updates to both documents/files maps in useEdgeworthDataStore/useEdgeworthDocumentsStore.
     * The 2 hooks are zustand stores used in useEdgeworthData and useSingletonEdgeworthDocuments respectively.
     * Files are objects that follow Edgeworth API schema, whereas documents are files parsed into data model that more closely align to our UX.
     */
    var updateDocument = useEdgeworthStoreDocumentUpdater();
    var updateFile = useEdgeworthDataStore().updateFile;
    var baseRequestData = useMemo(function () {
        if (!context || !identity || !config) {
            return undefined;
        }
        return getBaseRequestData(context, identity, config);
    }, [context, identity, config]);
    var onItemMutateCallback = useCallback(function (mutateEvent) {
        var docNewProps = {};
        var fileUpdater = undefined;
        if (mutateEvent.isPinning !== undefined) {
            // Favoriting / Unfavoriting
            docNewProps.isPinned = mutateEvent.isPinning;
            var currentDate = getCurrentDate();
            docNewProps.lastAccessedTime = currentDate;
            docNewProps.savedForLaterDateTime = mutateEvent.isPinning
                ? currentDate
                : undefined;
            fileUpdater = function (file) {
                file.is_pinned = !!docNewProps.isPinned;
                if (file.user_relationship) {
                    file.user_relationship.save_for_later_datetime =
                        docNewProps.savedForLaterDateTime;
                }
            };
        }
        else if (mutateEvent.isHiding !== undefined) {
            // Hiding / unhiding
            docNewProps.isHidden = mutateEvent.isHiding;
            fileUpdater = function (file) {
                file.is_hidden = !!docNewProps.isHidden;
            };
        }
        updateDocument(mutateEvent.document.id, docNewProps);
        if (fileUpdater) {
            updateFile(mutateEvent.document.id, fileUpdater);
        }
    }, [updateDocument, updateFile]);
    var pinMutation = useMutation(function (pinRequest) { return __awaiter(_this, void 0, void 0, function () {
        var isPinning, document, spoId, apiOptions;
        var _a;
        return __generator(this, function (_b) {
            isPinning = pinRequest.isPinning, document = pinRequest.document;
            // TODO: Should config-resolver-service resolveConfig throw rather than returning undefined?
            if (!config || !baseRequestData) {
                return [2 /*return*/, Promise.reject('Missing dependencies')];
            }
            spoId = getSpoId(document);
            apiOptions = __assign(__assign({}, baseRequestData), { isPinning: isPinning, pinApiPath: config.pinApiPath, unpinApiPath: config.unpinApiPath, workingSetId: document.workingSetId, app: document.app, url: document.mruUrl || document.url, mruType: document.mruType, mruId: document.mruId, resourceId: document.resourceId, lastSharedDateTime: (_a = document.lastSharedTime) === null || _a === void 0 ? void 0 : _a.toISOString() });
            if (spoId) {
                apiOptions.spoId = spoId;
            }
            return [2 /*return*/, isPinning ? pinItem(apiOptions) : unpinItem(apiOptions)];
        });
    }); }, {
        onMutate: function (pinRequest) {
            onItemMutateCallback(pinRequest);
            return pinRequest;
        },
        onError: function (error, pinRequest) {
            var handledError = handleEdgeworthRequestError(error, 'Pin');
            if (!handledError.continueSilently) {
                onItemMutateCallback({
                    document: pinRequest.document,
                    isPinning: !pinRequest.isPinning,
                });
            }
            return Promise.reject(handledError);
        },
    });
    var hideMutation = useMutation(function (hideRequest) { return __awaiter(_this, void 0, void 0, function () {
        var isHiding, document, spoId, apiOptions;
        var _a;
        return __generator(this, function (_b) {
            isHiding = hideRequest.isHiding, document = hideRequest.document;
            // TODO: Should config-resolver-service resolveConfig throw rather than returning undefined?
            if (!config || !baseRequestData) {
                return [2 /*return*/, Promise.reject('Missing dependencies')];
            }
            spoId = getSpoId(document);
            apiOptions = __assign(__assign({}, baseRequestData), { isHiding: isHiding, hideApiPath: config.hideApiPath, unhideApiPath: config.unhideApiPath, workingSetId: document.workingSetId, app: document.app, url: document.mruUrl || document.url, mruType: document.mruType, mruId: document.mruId, resourceId: document.resourceId, lastSharedDateTime: (_a = document.lastSharedTime) === null || _a === void 0 ? void 0 : _a.toISOString() });
            if (spoId) {
                apiOptions.spoId = spoId;
            }
            return [2 /*return*/, isHiding ? hideItem(apiOptions) : unhideItem(apiOptions)];
        });
    }); }, {
        onMutate: function (hideRequest) {
            onItemMutateCallback(hideRequest);
            return hideRequest;
        },
        onError: function (error, hideRequest) {
            var handledError = handleEdgeworthRequestError(error, 'Hide');
            if (!handledError.continueSilently) {
                onItemMutateCallback({
                    document: hideRequest.document,
                    isPinning: !hideRequest.isHiding,
                });
            }
            return Promise.reject(handledError);
        },
    });
    return useMemo(function () { return ({
        pinMutation: pinMutation,
        hideMutation: hideMutation,
    }); }, [hideMutation, pinMutation]);
}
//# sourceMappingURL=useMutateEdgeworthItem.js.map